import { useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { Icon, Alert } from '@polichat/flamboyant';
import { useSelector } from 'react-redux';
import { isMobile } from 'react-device-detect';
import { CustomerContactCreditContext } from 'src/context/CustomerContactCredit/context';
import { customerExpendCreditsPercentage } from '../../contexts/customerChannelBalance/useBalanceService/contactDayPercent/allChannels';
import {
  closeCreditAlert,
  showCreditAlert,
} from '../../../../../services/contacts-day/creditAlert';
import { EnumPercentages } from '../../contexts/customerChannelBalance/useBalanceService/contactDayPercent/enumPercentages';
import { useGlotaT } from 'src/hooks/useGlotaTranslation';

export interface ChannelCreditsAlertProps {
  channelId: number;
}

export function ChannelCreditsAlert({ channelId }: ChannelCreditsAlertProps) {
  const { data } = useContext(CustomerContactCreditContext);
  const isWhiteLabel = useSelector(
    (state: any) => state?.whitelabel?.config?.is_white_label
  );

  const [isOpenAlert, setIsOpenAlert] = useState(false);
  const [dismissedChannelId, setDismissedChannelId] = useState<number | null>(
    null
  );

  const poliWhatsappNumber = process.env.REACT_APP_POLI_WHATSAPP_NUMBER;
  const linkPoliWhats = `https://wa.me/${poliWhatsappNumber}`;

  // Calcula porcentagem apenas quando data ou channelId mudam
  const channelPercentage = useMemo(() => {
    if (!data || !channelId) return { hasPercentage: false };
    return customerExpendCreditsPercentage(channelId, data);
  }, [data, channelId]);

  const percentage = channelPercentage.hasPercentage
    ? channelPercentage.percentageValue || 0
    : 0;

  // Obtém o nome do canal
  const channelName = useMemo(() => {
    if (!data || !channelId) return '';
    const channelData = (data as any)[channelId];
    return channelData?.channelName || `Canal ${channelId}`;
  }, [data, channelId]);

  const wL80percent = percentage >= 80 && isWhiteLabel;
  const wL100percent = percentage >= 100 && isWhiteLabel;

  /** Translations  */
  const title = useGlotaT('spa.chat.creditAlert.title');
  const attention = useGlotaT('spa.chat.creditAlert.attention');
  const fullyConsumed = useGlotaT('spa.chat.creditAlert.fullyConsumed');
  const consumedMessage = useGlotaT('spa.chat.creditAlert.consumedMessage');
  const whitelabelConsumedMessage = useGlotaT(
    'spa.chat.creditAlert.whitelabelConsumedMessage'
  );
  const whitelabelFallbackMessage = useGlotaT(
    'spa.chat.creditAlert.whitelabelFallbackMessage'
  );
  const whitelabeFullConsumedMessage = useGlotaT(
    'spa.chat.creditAlert.whitelabeFullConsumedMessage'
  );
  const contactTeam = useGlotaT('spa.chat.creditAlert.contactTeam');
  const avoidExtraCharges = useGlotaT('spa.chat.creditAlert.avoidExtraCharges');
  const buyNewCredits = useGlotaT('spa.chat.creditAlert.buyNewCredits');

  const getConsumedMessage = useCallback(() => {
    if (percentage >= 100 && !isWhiteLabel)
      return fullyConsumed.replace('{channelName}', channelName);

    if (percentage >= 80 && percentage < 100 && !isWhiteLabel) {
      return consumedMessage
        .replace('{percentage}', percentage.toString())
        .replace('{channelName}', channelName);
    }
    if (wL80percent) {
      return whitelabelConsumedMessage
        .replace('{percentage}', percentage.toString())
        .replace('{channelName}', channelName);
    }
    return whitelabelFallbackMessage.replace('{channelName}', channelName);
  }, [percentage, isWhiteLabel, wL80percent, channelName]);

  const getColor = useCallback(() => {
    if (percentage >= 100) {
      return 'danger';
    }
    if (wL80percent) {
      return 'warning';
    }
    return null;
  }, [percentage, wL80percent]);

  const getQuantityPercentageNumber = useCallback((perc: number) => {
    if (perc >= 100) return 100;
    if (perc >= 80 && perc < 100) return 80;
    return null;
  }, []);

  const closeAlert = useCallback(async () => {
    setIsOpenAlert(false);
    setDismissedChannelId(channelId);

    if (!isWhiteLabel) {
      const quantity_alert = getQuantityPercentageNumber(percentage);
      if (channelId && quantity_alert) {
        await closeCreditAlert(channelId, quantity_alert);
      }
    }
  }, [isWhiteLabel, percentage, channelId, getQuantityPercentageNumber]);

  // Verifica e abre alerta quando necessário
  useEffect(() => {
    // Se o alerta foi dismissado para este canal específico, não mostrar
    if (dismissedChannelId === channelId || !channelPercentage.hasPercentage) {
      setIsOpenAlert(false);
      return;
    }

    if (percentage >= EnumPercentages.EIGHTY) {
      const quantity_alert = getQuantityPercentageNumber(percentage);
      if (channelId && quantity_alert) {
        showCreditAlert(channelId, quantity_alert).then((canOpen) => {
          if (canOpen) {
            setIsOpenAlert(true);
          }
        });
      }
    } else {
      setIsOpenAlert(false);
    }
  }, [
    percentage,
    channelId,
    dismissedChannelId,
    channelPercentage.hasPercentage,
    getQuantityPercentageNumber,
  ]);

  const styles = {
    container: {
      padding: '0.5rem 1rem',
      marginBottom: '0',
      borderRadius: '0',
      display: 'flex',
      flexDirection: isWhiteLabel ? 'column' : 'row',
      alignItems: isWhiteLabel ? 'flex-start' : 'center',
      fontSize: '0.8rem',
      zIndex: 2,
      ...(isWhiteLabel
        ? { backgroundColor: '#fff8f4' }
        : {
            display: 'flex',
            gap: '8px',
            backgroundColor: '#FAE0AA',
          }),
    },
    closeIcon: {
      position: 'absolute' as const,
      top: '0.5rem',
      right: '0.5rem',
      cursor: 'pointer',
    },
    title: {
      color: isWhiteLabel ? 'inherit' : '#232C3D',
      display: 'flex',
      alignItems: 'flex-end',
    },
    text: {
      color: isWhiteLabel ? 'inherit' : '#232C3D',
      margin: '0',
    },
  };

  if (!isOpenAlert) return null;

  return (
    <>
      {wL100percent && (
        <Alert style={styles.container} color={getColor()}>
          <h3 style={styles.title}>
            <Icon color="danger" size={24} icon="poli-icon pi-alert-fill" />
            <b>{attention}</b>
          </h3>
          <p
            style={{
              ...styles.text,
              wordBreak: 'break-word',
              whiteSpace: 'normal',
              maxWidth: '350px',
            }}
          >
            {whitelabeFullConsumedMessage}
          </p>
        </Alert>
      )}
      {!wL100percent && (
        <Alert style={styles.container} color={getColor()}>
          <div style={styles.closeIcon} onClick={closeAlert}>
            <Icon
              style={styles.closeIcon}
              icon="poli-icon pi-close-line"
              color="#ffa26b"
              size={20}
            />
          </div>
          {!isMobile && !isWhiteLabel && (
            <h3 style={styles.title}>
              <Icon color="#FFCA5D" size={24} icon="poli-icon pi-alert-line" />
            </h3>
          )}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
              maxWidth: '350px',
              wordBreak: 'break-word',
              whiteSpace: 'normal',
            }}
          >
            <h3 style={styles.title}>{title}</h3>
            <p style={styles.text}>
              {getConsumedMessage()}
              {!isWhiteLabel && percentage >= 100 && (
                <>
                  <a href={linkPoliWhats} target="_blank" rel="noreferrer">
                    {contactTeam}
                  </a>{' '}
                  {avoidExtraCharges}
                </>
              )}
              {wL80percent && (
                <span style={{ display: 'flex', wordBreak: 'break-word' }}>
                  {buyNewCredits}
                </span>
              )}
            </p>
          </div>
        </Alert>
      )}{' '}
    </>
  );
}
